import { ReactNode } from 'react';
import { useAuth } from 'contexts/AuthContext';

interface PermissionGuardProps {
  permissions?: string | string[];
  /**
   * Aceita se o user tem qualquer permissão começando com algum destes prefixos (ex.: `"crm."`).
   * String única continua valendo.
   */
  permissionsPrefix?: string | string[];
  children: ReactNode;
  fallback?: ReactNode;
  all?: boolean;
}

/**
 * Libera o conteúdo quando a pessoa satisfaz **algum** dos critérios declarados.
 *
 * **Permissões e prefixos combinam em OR, de propósito.** Os dois são formas de escrever o mesmo
 * pedido ("quem pode ver isto?"), e um grupo de rotas pode legitimamente ter as duas naturezas: o
 * grupo `/admin` abriga telas sob `admin.*` e a de contas de serviço, cuja permissão
 * (`auth.servicos.manage`) mora no Auth porque o que ela libera é emitir credencial. Com AND, um
 * papel estreito que tivesse só essa permissão e nenhuma `admin.*` levaria "Sem acesso" antes de
 * chegar à rota que ele tem direito de abrir.
 *
 * `all` continua valendo dentro de `permissions` (exigir todas daquela lista) e não atravessa a
 * fronteira para os prefixos — quem precisa de conjunção entre critérios diferentes aninha dois
 * guards, que é mais fácil de ler do que uma flag combinando quatro casos.
 */
const PermissionGuard = ({ permissions, permissionsPrefix, children, fallback = null, all = false }: PermissionGuardProps) => {
  const { user, hasPermission } = useAuth();

  if (!permissions && !permissionsPrefix) return <>{children}</>;

  const prefixos = permissionsPrefix ? (Array.isArray(permissionsPrefix) ? permissionsPrefix : [permissionsPrefix]) : [];
  const perms = permissions ? (Array.isArray(permissions) ? permissions : [permissions]) : [];

  // Lista vazia explícita (`permissions={[]}`) sempre liberou, e continua: é como se diz "sem
  // exigência" sem remover a prop.
  if (prefixos.length === 0 && perms.length === 0) return <>{children}</>;

  const userPerms = user?.permissions ?? [];
  const porPrefixo = prefixos.some((prefixo) => userPerms.some((p) => p.startsWith(prefixo)));
  const porPermissao = perms.length > 0 && (all ? perms.every((p) => hasPermission(p)) : perms.some((p) => hasPermission(p)));

  return porPrefixo || porPermissao ? <>{children}</> : <>{fallback}</>;
};

export default PermissionGuard;
